/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { FancyDatabase, IFancyServerValue, ISnapshotLike } from '@touchplan/common';
// import { DatabaseReference } from "firebase/database";
import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import { FancyRef } from '@touchplan/common/lib/FancyRef';

import { Observable } from 'rxjs';

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6750
// as it stands, this class currently _only_ works with compat references
// undecided if it's worth supporting both, or just mass migrating to modular in one shot

// TODO - do better
// this is mostly just to make it easier to swap between compat and modular for initial testing
type CompatAgnosticReference = firebase.database.Reference;
// type CompatAgnosticReference = DatabaseReference;
// END PLACEHOLDER REPLACE AS PART OF TP-6750
/**
 * Client sdk specific abstraction of the firebase/database sdk. Replacing the firebase
 * reference with our own [[FancyRef]].
 * Initial focus is the ref, but can eventually be extended to every other rtdb method
 */
export class FancyClientDatabase extends FancyDatabase<CompatAgnosticReference> {
	public readonly ServerValue: IFancyServerValue = firebase.database.ServerValue;

	protected getSdkReference(path: string, partition?: string): CompatAgnosticReference {
		return firebase.app().database(partition).ref(path);
	}

	public set(ref: FancyRef, data: unknown): Promise<void> {
		return this.toFirebaseRef(ref).set(data);
	}

	public update(ref: FancyRef, data: unknown): Promise<void> {
		//it's wild that firebase's types don't match reality
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return this.toFirebaseRef(ref).update(data as any);
	}

	public child(ref: FancyRef, path: string): FancyRef {
		//a _little_ silly, but _safe_
		return this.toFancyRef(this.toFirebaseRef(ref).child(path), ref.partition);
	}

	public get<T = unknown>(ref: FancyRef): Promise<ISnapshotLike<T>> {
		return this.toFirebaseRef(ref).once('value');
	}

	//BEGIN PLACEHOLDER REPLACE AS PART OF TP-6696
	// ...spend some more time thinking about this
	// this _could_ be angularfire on desktop, but this might not be desktop
	// it could also be rxfire, but rxfire is seeming progressively more useless.
	// decisions, decisions
	public object$<T>(ref: FancyRef): Observable<ISnapshotLike<T>> {
		const fbRef = this.toFirebaseRef(ref);

		// absolutely disgusting, essentially rewrite one of the rxfire methods
		// because rxfire continues to be incompatible with firebase-admin
		return new Observable<ISnapshotLike<T>>((subscriber) => {
			const onReference = fbRef.on(
				'value',
				(snapshot) => {
					subscriber.next(snapshot);
				},
				(e) => {
					subscriber.error(e);
				}
			);

			return () => {
				fbRef.off('value', onReference);
			};
		});
	}
	//END PLACEHOLDER REPLACE AS PART OF TP-6696

	public generateChildKey(ref: FancyRef): string {
		//key can only return null when used at the root, but push will never be at the root
		return this.toFirebaseRef(ref).push().key!;
	}

	/**
	 * Query a list of firebase objects, using a child key.
	 * This is the most common query we use (and I don't really want to implement 100% query support right now)
	 */
	public getIndexedChildValue<T = unknown>(
		ref: FancyRef,
		childKey: string,
		childValue: string
	): Promise<ISnapshotLike<T>> {
		return this.toFirebaseRef(ref).orderByChild(childKey).equalTo(childValue).limitToFirst(1).once('value');
	}
}
export const fancyDatabase: FancyClientDatabase = new FancyClientDatabase();
